import { useCallback, useEffect, useSyncExternalStore } from "react";

/**
 * Appearance is a SUITE setting (shared-theme plan): one value stored under
 * `suite.appearance`, owned by the shared runtime (`window.SuiteTheme`,
 * served as `/suite-theme.js` from the hub). This hook binds sak's React
 * surface to that runtime — the runtime persists and applies, this hook keeps
 * the render in step. While the runtime is unreachable the hook falls back to
 * the same storage contract directly (`hat.theme` was migrated into
 * `suite.appearance` on first boot), so appearance never breaks.
 */

/** Apple HIG theme switching: light / dark / system with OS-follow fallback. */
export type ThemeMode = "light" | "dark" | "system";

/** The slice of window.SuiteTheme this hook talks to. */
type SuiteThemeApi = {
  mode: string;
  setMode: (mode: string, own?: boolean) => string;
  onChange: (listener: (state: { mode: string; accent: string }) => void) => () => void;
};

function runtime(): SuiteThemeApi | null {
  const api = (window as unknown as { SuiteTheme?: SuiteThemeApi }).SuiteTheme;
  return api && typeof api.setMode === "function" ? api : null;
}

const STORAGE_KEY = "suite.appearance";
const LEGACY_KEY = "hat.theme";

function isThemeMode(value: string | null | undefined): value is ThemeMode {
  return value === "light" || value === "dark" || value === "system";
}

function readStored(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_KEY);
    if (isThemeMode(stored)) return stored;
  } catch {
    // Storage unavailable (hardened WebView): fall through to system.
  }
  return "system";
}

/** The theme is one value, not one value per component: a module-level store
 *  keeps the topbar, the in-app switcher and any future call site in lockstep. */
let current: ThemeMode | null = null;
const listeners = new Set<() => void>();

function resolveInitial(): ThemeMode {
  const api = runtime();
  if (api && isThemeMode(api.mode)) return api.mode;
  return readStored();
}

function read(): ThemeMode {
  if (current === null) current = resolveInitial();
  return current;
}

/** Stable identity per value: a string, so getSnapshot never re-renders on its own. */
function getSnapshot(): ThemeMode {
  return read();
}

function write(next: ThemeMode): void {
  if (current === next) return;
  current = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function applyTheme(mode: ThemeMode) {
  const root = document.documentElement;
  // Reference contract: the attribute is REMOVED in system mode and the CSS
  // media queries take over. The runtime writes the same attribute; writing
  // it here too keeps the document in step even while the runtime is
  // unreachable. `color-scheme` is owned by the canonical sheet.
  if (mode === "system") {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", mode);
  }
}

export function useTheme() {
  const mode = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    applyTheme(mode);
    const api = runtime();
    if (api) {
      // The runtime owns persistence and application; only push what differs,
      // then stay in step with every other path that moves it (the bar's
      // quick-pick, another suite window's storage event, a settings surface).
      if (api.mode !== mode) api.setMode(mode);
      const unsubscribe = api.onChange((state) => {
        if (isThemeMode(state.mode)) write(state.mode);
      });
      return unsubscribe;
    }
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Non-fatal: this session still applies the choice.
    }
  }, [mode]);

  const cycle = useCallback(() => {
    write(mode === "light" ? "dark" : mode === "dark" ? "system" : "light");
  }, [mode]);

  return { mode, setMode: write, cycle };
}
